<template>
  <div 
    class="comment d-flex mx-n2 px-2 py-75"
    :class="commentHighlighted ? 'comment-highlighted' : ''"
  >
    <!-- User avatar -->
    <b-link :to="getMemberLocation()">
      <user-avatar :user="member" class="comment__avatar" />
    </b-link>

    <!-- Comment content -->
    <div class="comment__content mx-1">
      <div>
        <!-- User who did the comment -->
        <b-link :to="getMemberLocation()">
          <h6 class="mb-0">
            {{ member.name }} {{ member.surname }}
          </h6>
        </b-link>   

        <!-- When the comment was made -->
        <small
          class="text-muted"
          :title="exactDate"
          @mouseover="hover = true"
          @mouseleave="hover = false"
        >
          {{ date }}
        </small>

        <!-- The comment content -->
        <div v-if="!isEditing" class="mt-50">
          <content-with-anchors 
            :content="comment.content" 
          />

          <!-- Moderation -->
          <moderation-section
            :content="comment"
            :morph-type="morphTypePlural"
            item-type="comments"
            item-type-name="comment"
            :parent-key="sourceKey"
            :can-moderate="canModerate"
            @moderation-complete="handleModerationComplete"
          />
        </div>

        <!-- Edit comment form -->
        <comment-form
          v-else
          ref="commentForm"
          :source-key="comment.key"
          morph-type="comment"
          :item-type="itemType"
          :is-editing="true"
          :comment="comment"
          @update="isEditing = false"
          @cancel="isEditing = false"
        />

        <!-- Social -->
        <div class="comment__social mt-1 d-flex">
          <button-like
            :liked="comment.likedByMember"
            :likes-count="comment.totalLikes"
            @click="$emit('toggle-like', comment)"
          />
          <button-comment
            v-if="comment.childCommentsCount"
            class="ml-1"
            :comments-count="comment.childCommentsCount"
            :active="isShowingResponses"
            @click="isShowingResponses = !isShowingResponses"
          />
          <div
            v-if="!leaf"
            variant="link"
            size="sm"
            class="text-uppercase button-reply pointer ml-1"
            @click="handleReply"
          >
            {{ $t('social.reply') }}
          </div>
        </div>

        <!-- Nested comments section -->
        <div v-if="!leaf">
          <comments-section
            v-if="isShowingResponses"
            :source-key="comment.key"
            :can-moderate="canModerate"
            morph-type="comment"
            :item-type="itemType"
            :leaf="true"
            :embedded="true"
            :comment-key-highlighted="subCommentKeyHighlighted"
            @comment-created="$emit('commented', comment)"
            @comment-deleted="deleteComment"
          />
        </div>
      </div>
    </div>

    <!-- Action buttons -->
    <div 
      v-if="isLoggedMember" 
      class="comment__action-buttons position-absolute"
    >
      <button-edit
        @click="isEditing=true"
      />
      <button-delete
        class="ml-75"
        @click="handleRemoveItem"
      />
    </div>
  </div>
</template>

<script>
import UserAvatar from '@core/components/user/UserAvatar.vue';
import ButtonLike from '@core/components/social/ButtonLike.vue';
import ButtonEdit from '@core/components/social/ButtonEdit.vue';
import ButtonDelete from '@core/components/social/ButtonDelete.vue';
import ButtonComment from '@core/components/social/ButtonComment.vue';
import ContentWithAnchors from '@core/components/string-utils/ContentWithAnchors.vue';
import CommentsSection from './CommentsSection.vue';
import ModerationSection from '@core/components/moderation/ModerationSection.vue';
import ToastNotificationsMixin from '@core/mixins/toast-notifications/ToastNotificationsMixin';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import { formatExactDateTime } from '@/utils/dateFormatter';

export default {
  name: 'CommentItem',
  components: {
    UserAvatar,
    ButtonLike,
    ButtonComment,
    ContentWithAnchors,
    CommentsSection,
    ButtonDelete,
    ButtonEdit,
    ModerationSection,
  },
  mixins: [ToastNotificationsMixin],
  props: {
    comment: {
      type: Object,
      required: true,
    },
    leaf: Boolean,
    itemType: {
      type: String,
      default: null,
    },
    morphType: {
      type: String,
      default: null,
    },
    sourceKey: {
      type: String,
      required: true,
    },
    canModerate: {
      type: Boolean,
      default: false,
    },
    /**
     * The key of the comment to highlight
     */
    commentHighlighted: {
      type: Boolean,
      default: false,
    },
    /**
     * The key of the sub-comment to highlight (for nested comments)
     */
    subCommentKeyHighlighted: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isShowingResponses: false,
      isEditing: false,
    };
  },
  computed: {
    user() {
      return this.comment.createdBy;
    },
    member() {
      return this.comment.createdByMember;
    },
    locale() {
      return this.$store.state.locale.currentLocale;
    },
    isLoggedMember() {
      return this.$store.getters.loggedMember.key === this.comment.createdByMember.key;
    },
    exactDate() {
      const date = new Date(this.comment.createdAt);
      return date.toLocaleString();
    },
    date() {
      return formatExactDateTime(this.comment.createdAt, this.locale);
    },
    morphTypePlural() {
      return `${this.morphType}s`;
    },
    translate(field) {
      return translateTranslationTable(this.locale, field);
    },
    shouldShowResponses() {
      // Auto-expand replies when this comment is the highlighted parent (commentKey)
      // This covers URLs that include both commentKey and subCommentKey
      return !!this.commentHighlighted;
    },
  },
  mounted() {
    // Automatically show responses if this comment should be expanded
    if (this.shouldShowResponses) {
      this.isShowingResponses = true;
    }
  },
  methods: {
    async handleReply() {
      this.isShowingResponses = !this.isShowingResponses;
    },
    async handleModerationComplete({ action, contentKey, response }) {
      if (response) {
        // console.log('handleModerationComplete response', response);
        Object.assign(this.comment, response);
      }
      
      this.$emit('moderation-complete', {
        action: action,
        contentKey: contentKey
      });
    },
    deleteComment() {
      --this.comment.childCommentsCount;
    },
    handleRemoveItem(response) {
      if (response === true) {
        try {
          this.$store.dispatch('deleteComment', {
            item: {
              storedKey: this.sourceKey,
              itemType: 'comments',
              requestConfig: {
                key: this.comment.key,
              },
            },
          });
          this.$emit('deleted');
          this.notifySuccess(this.$t('streaming.messages.success-delete'));
        } catch (error) {
          this.notifyError(this.$t('streaming.messages.error-delete'));
        }
      }
    },
    getMemberLocation() {
      if (!this.user.name) {
        return null;
      }

      return {
        name: 'member-details',
        params: {
          username: this.user.username,
          communityId: this.$route.params.communityId,
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-include'; // Bootstrap includes

.button-reply {
  color: $dark;
  @include hover() {
    color: $primary;
  }
  &:focus,
  &.focus {
    box-shadow: 0 0 0 $btn-focus-width rgba($dark, .5);
  }

  &.disabled,
  &:disabled {
    color: $text-muted;
  }

/*   &:not(:disabled):not(.disabled):active,
  &:not(:disabled):not(.disabled).active {
    background: $primary;
    color: $white;
  } */

  &--active {
    color: $primary;
    @include hover() {
      color: $dark;
    }
    &:focus,
    &.focus {
      box-shadow: 0 0 0 $btn-focus-width rgba($primary, .5);
    }

    &.disabled,
    &:disabled {
      color: $text-muted;
    }

/*     &:not(:disabled):not(.disabled):active,
    &:not(:disabled):not(.disabled).active {
      background: $dark;
      color: $white;
    } */
  }
}
  .comments-count {
    display: inline-block;
    line-height: 1.5;
    margin-left: calc($spacer / 2);
  }
  .comment {
    &__content {
      flex-grow: 1;
    }
    &__action-buttons{
      display:none;
      right: 2.5rem;
      :hover > & {
        display:flex;
      }
    }
    &:hover{
      background-color: #f9f9f9;
    }
    
    // Special styling for highlighted comments
    &.comment-highlighted {
      border: 2px solid $primary;
      border-radius: 8px;
      background-color: rgba($primary, 0.05);
      
      &:hover {
        background-color: rgba($primary, 0.1);
      }
    }
  }
</style>
